import React, { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import config from "../../config";
import { clearRunCache } from "./useRunData";
import { Spinner } from "./Pending";
import PromptListEditor from "../../components/PromptListEditor";
import ScheduleEditor from "../../components/ScheduleEditor";
import CostBreakdown from "./CostBreakdown";
import "./TabSettings.css";

export default function TabSettings() {
  const { report, reportId, refresh, exportServiceAccountEmail } = useOutletContext();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState((report.prompts || []).map((p) => p.text));
  const [runsPerPrompt, setRunsPerPrompt] = useState(report.runsPerPrompt || 5);
  const [schedule, setSchedule] = useState({
    intervalDays: report.schedule?.intervalDays ?? 7,
    dayOfWeek: report.schedule?.dayOfWeek ?? 1,
    dayOfMonth: report.schedule?.dayOfMonth ?? 1,
  });
  const [models, setModels] = useState(report.models || []);
  const [clientName, setClientName] = useState(report.clientName || "");
  const [clientDomains, setClientDomains] = useState(
    report.clientDomains?.length ? report.clientDomains : (report.clientDomain ? [report.clientDomain] : [])
  );
  const [clientAliases, setClientAliases] = useState(report.clientAliases || []);
  const [competitors, setCompetitors] = useState(report.competitors || []);
  const [rescoring, setRescoring] = useState(false);
  const [exportFolderUrl, setExportFolderUrl] = useState(report.exportFolderUrl || "");
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [cost, setCost] = useState(null);
  const [costLoading, setCostLoading] = useState(false);

  // Live estimate for the settings as currently edited (not necessarily saved).
  const modelsKey = models.join(",");
  useEffect(() => {
    if (models.length === 0 || prompts.length === 0) { setCost(null); return; }
    let cancelled = false;
    setCostLoading(true);
    fetch(`${config.api.baseUrl}/gpt-seo/calculate-price`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        models, numPrompts: prompts.length, runsPerPrompt, intervalDays: schedule.intervalDays,
      }),
    })
      .then((r) => r.json())
      .then((d) => { if (!cancelled && d.success) setCost(d); })
      .catch(() => { /* noop */ })
      .finally(() => { if (!cancelled) setCostLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelsKey, prompts.length, runsPerPrompt, schedule.intervalDays]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${config.api.baseUrl}${config.api.endpoints.dashboard.models}`, { credentials: "include" });
        const d = await r.json();
        if (d.success) setAvailableModels((d.data || []).filter((m) => m.showOnGeoDashboard));
      } catch (e) { /* noop */ }
      finally { setModelsLoading(false); }
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true); setMsg(""); setErr("");
    try {
      const r = await fetch(`${config.api.baseUrl}/gpt-seo/reports/${reportId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          clientName, clientDomains, clientAliases, competitors, exportFolderUrl,
          prompts, runsPerPrompt, schedule, models,
        }),
      });
      const d = await r.json();
      if (d.success) { setMsg("Saved."); refresh(); return true; }
      setErr(d.error || "Save failed.");
    } catch (e) { setErr("Network error."); }
    finally { setSaving(false); }
    return false;
  };

  // Save, then replay the name/alias/domain/competitor rules over every stored
  // run. Deterministic only (no AI calls), so it finishes in about a second.
  const saveAndRescore = async () => {
    if (!(await save())) return;
    setRescoring(true);
    try {
      const r = await fetch(`${config.api.baseUrl}/gpt-seo/reports/${reportId}/rescore`, {
        method: "POST", credentials: "include",
      });
      const d = await r.json();
      if (d.success) { clearRunCache(); setMsg(`Saved and re-scored ${d.runsRescored} past run${d.runsRescored === 1 ? "" : "s"}.`); refresh(); }
      else setErr(d.error || "Re-score failed.");
    } catch (e) { setErr("Network error."); }
    finally { setRescoring(false); }
  };

  const doDelete = async () => {
    const r = await fetch(`${config.api.baseUrl}/gpt-seo/reports/${reportId}`, {
      method: "DELETE", credentials: "include",
    });
    const d = await r.json();
    if (d.success) navigate("/gpt-seo-tracking");
    else setErr(d.error || "Delete failed.");
  };

  return (
    <div className="tab-settings">
      {msg && <div className="success-message">{msg}</div>}
      {err && <div className="error-message">{err}</div>}

      <section>
        <h3>Client</h3>
        <input
          type="text"
          className="tab-settings-text"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          placeholder="Client name"
          aria-label="Client name"
        />
        <p className="tab-settings-help">
          Brands and alternate names that also count as the client. Without these, a parent
          company&apos;s brands get counted as competitors.
        </p>
        <PromptListEditor
          prompts={clientAliases}
          onChange={setClientAliases}
          noun="brand names"
          max={20}
          placeholder={"One brand or alternate name per line.\nUptown Cheapskate\nKid to Kid"}
        />
        <p className="tab-settings-help">
          Domains and subdomains that count as the client when a response links out.
        </p>
        <PromptListEditor
          prompts={clientDomains}
          onChange={setClientDomains}
          noun="domains"
          max={20}
          placeholder={"One domain per line.\nacme.com\nshop.acme.com"}
        />
      </section>

      <section>
        <h3>Prompts</h3>
        <PromptListEditor prompts={prompts} onChange={setPrompts} />
      </section>

      <section>
        <h3>Competitors</h3>
        <p className="tab-settings-help">
          Leave empty to let the AI discover competitors. When set, only these competitors are
          tracked, from the next run onward.
        </p>
        <PromptListEditor
          prompts={competitors}
          onChange={setCompetitors}
          noun="competitors"
          max={50}
          placeholder={"One competitor per line.\nBeta Corp\nGamma Inc"}
        />
      </section>

      <section>
        <h3>Models</h3>
        <div className="tab-settings-models">
          {modelsLoading && availableModels.length === 0 && <Spinner size={20} />}
          {availableModels.map((m) => (
            <label key={m.id} className="tab-settings-model-checkbox">
              <input
                type="checkbox"
                checked={models.includes(m.id)}
                onChange={() => setModels((ms) =>
                  ms.includes(m.id) ? ms.filter((x) => x !== m.id) : [...ms, m.id]
                )}
              />
              <span>{m.name}</span>
            </label>
          ))}
        </div>
      </section>

      <section>
        <h3>Runs per prompt</h3>
        <input
          type="number"
          min="1"
          max="20"
          className="tab-settings-number"
          value={runsPerPrompt}
          onChange={(e) => setRunsPerPrompt(Math.max(1, Math.min(20, parseInt(e.target.value || "1", 10))))}
        />
      </section>

      <section>
        <h3>Schedule</h3>
        <ScheduleEditor value={schedule} onChange={setSchedule} />
      </section>

      <section>
        <h3>Estimated cost</h3>
        <p className="tab-settings-help">
          For the models, prompts and runs per prompt shown above. Includes each provider&apos;s
          web search fee and the gpt-5-nano calls that read and score every response.
        </p>
        {models.length === 0 || prompts.length === 0
          ? <p className="tab-settings-help">Select at least one model and one prompt.</p>
          : <CostBreakdown cost={cost} loading={costLoading} />}
      </section>

      <section>
        <h3>Google Drive auto-export</h3>
        <p className="tab-settings-help">
          Paste a Drive folder link to get a Google Sheet of every run there.
          {exportServiceAccountEmail && (
            <> Share the folder with <code>{exportServiceAccountEmail}</code> as Editor.</>
          )}
          {" "}Leave empty to disable.
        </p>
        <input
          type="url"
          className="tab-settings-text"
          value={exportFolderUrl}
          onChange={(e) => setExportFolderUrl(e.target.value)}
          placeholder="https://drive.google.com/drive/folders/…"
          aria-label="Google Drive folder URL"
        />
      </section>

      <div className="tab-settings-save-row">
        <button className="tab-settings-save-btn" onClick={save} disabled={saving || rescoring}>
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button
          className="tab-settings-cancel-btn"
          onClick={saveAndRescore}
          disabled={saving || rescoring || !(report.runs || []).some((r) => r.status === "completed")}
          title="Re-apply the client name, brand names, domains and competitor list to every past run. No AI calls."
        >
          {rescoring ? "Re-scoring…" : "Save & re-score past runs"}
        </button>
      </div>

      <hr className="tab-settings-divider" />

      <section>
        <h3>Danger zone</h3>
        {!confirmDelete ? (
          <button className="tab-settings-delete-btn" onClick={() => setConfirmDelete(true)}>
            Delete report
          </button>
        ) : (
          <div className="tab-settings-confirm-row">
            <button className="tab-settings-delete-confirm" onClick={doDelete}>Confirm delete</button>
            <button className="tab-settings-cancel-btn" onClick={() => setConfirmDelete(false)}>Cancel</button>
          </div>
        )}
      </section>
    </div>
  );
}
